import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import { tournamentService } from '../../services/tournamentService';
import { filterPublicCompetitions } from '../../utils/publicCompetitionVisibility';

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'upcoming') return 'bg-primary/10 text-primary';
  if (status === 'cancelled') return 'bg-destructive/10 text-destructive';
  return 'bg-muted text-muted-foreground';
};

const formatDate = (isoDate) => {
  if (!isoDate) return null;
  return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const TournamentsListPage = () => {
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Tournaments - Tanzania Basketball Federation';
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tournamentService.getTournaments();
      setTournaments(filterPublicCompetitions(data || []));
    } catch (err) {
      setError(err.message || 'Failed to load tournaments');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTournaments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tournaments;
    return tournaments.filter((tournament) =>
      [tournament?.name, tournament?.season, tournament?.description]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [tournaments, search]);

  return (
    <PublicBrowseShell title="Tournaments" subtitle="Regional basketball tournaments and knockout competitions">
      <div className="mb-6 max-w-md">
        <Input
          type="search"
          placeholder="Search tournaments..."
          iconName="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading tournaments...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : filteredTournaments?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Award" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Tournaments Found</h3>
          <p className="text-muted-foreground">Try adjusting your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTournaments.map((tournament) => (
            <Link
              key={tournament.id}
              to={`/tournaments/${tournament.id}`}
              className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-lg bg-amber-600/10 flex items-center justify-center shrink-0">
                  <Icon name="Award" size={26} className="text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">{tournament?.name || 'Unnamed Tournament'}</h3>
                  <p className="text-sm text-muted-foreground truncate">{tournament?.season}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{formatDate(tournament?.startDate) || 'Date TBD'}</span>
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${statusBadgeClass(tournament?.tournamentStatus)}`}>
                  {tournament?.tournamentStatus || 'upcoming'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default TournamentsListPage;
